import { useNavigate } from '@tanstack/react-router';
import { CalendarPlus } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { SkeletonLines } from '../../../components/Skeleton';
import { EmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { useCanView } from '../../../stores/session';
import { useInterviews } from '../api/interviews-api';
import { InterviewRow } from '../components/InterviewRow';
import { InterviewsHeader } from '../components/InterviewsHeader';
import { useViewerZone } from '../hooks/use-interview-actions';
import styles from '../components/Interviews.module.css';

/**
 * SCH-02's other half: every interview still waiting for a time — never scheduled, times
 * sent and not yet chosen, or being moved — on applicants still in that stage. Oldest
 * first, since they have waited longest (N-36 is about exactly these).
 */
export function ToSchedulePage(): ReactNode {
  const canView = useCanView('job_config');
  const zone = useViewerZone();
  const navigate = useNavigate();
  const query = useInterviews({ view: 'to_schedule' });

  if (!canView) return <ForbiddenState resource="scheduling interviews" contact="Your admin" />;

  return (
    <div className={styles.screen}>
      <InterviewsHeader current="to_schedule" />
      <Card title="To schedule" meta="Interviews waiting for a time, longest-waiting first">
        {query.isPending ? (
          <SkeletonLines count={6} />
        ) : query.error ? (
          <ServerErrorState onRetry={() => void query.refetch()} />
        ) : query.data.length === 0 ? (
          <EmptyState
            isInset
            title="Nothing waiting"
            body="When someone reaches an interview stage, their interview appears here to be scheduled."
            action={
              <Button variant="outline" size="sm" onClick={() => void navigate({ to: '/jobs' })}>
                Open jobs
              </Button>
            }
          />
        ) : (
          <ul className={styles.list}>
            {query.data.map((row) => (
              <InterviewRow
                key={row.id}
                interview={row}
                zone={zone}
                hasDateTile
                action={
                  <Button size="sm" variant={row.status === 'slots_offered' ? 'ghost' : 'solid'} icon={CalendarPlus} onClick={() => void navigate({ to: '/interviews/$interviewId/schedule', params: { interviewId: row.id } })}>
                    {row.status === 'slots_offered' ? 'Change the times' : 'Schedule'}
                  </Button>
                }
              />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
